import React from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogTitle, Stack, Typography } from '@mui/material';

import { useDeleteCommission } from '~/hooks/apis/agent/useDeleteCommission';

import { Commission } from '~/types/services/agentHub';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: Commission;
}

export default function DeleteCommissionDialog({ isOpen, onClose, data }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const deleteCommissionMutation = useDeleteCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule deleted', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });

  function handleDeleteCommission(id: string) {
    deleteCommissionMutation.mutate(id, {
      onSuccess: () => {
        onClose();
      },
    });
  }

  const isDeleting = deleteCommissionMutation.isPending;

  return (
    <Dialog open={isOpen} fullWidth onClose={onClose}>
      <DialogTitle>Confirm deletion</DialogTitle>
      <Stack direction="column" paddingX={3} paddingBottom={3} spacing={2}>
        <Typography>
          Are you sure you wish to delete {data?.description}? You'll need to set up this commission rule again if
          required.
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={onClose} variant="text" disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleDeleteCommission(data.id)} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Yes, delete'}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
